import { ReactNode } from "react";
import { Box, Grid, IconButton } from "@mui/material";
import { IconMessageFilled } from "@tabler/icons-react";
import MxTypography from "@rf-l1/MxTypography";
import MxIcons from "@rf-l1/MxIcons";
import MxImageBlur from "@rf-l1/images/MxImageBlur";

export interface IMxPersonCardProps {
  /** Nombre de la persona */
  name: string;
  /** Rol o etiqueta (ej: "Propietario", "Agente") */
  role: string;
  /** Número de teléfono */
  phone: string;
  /** Email para el botón de mensaje */
  email?: string;
  /** URL de la imagen del avatar (opcional) */
  avatarUrl?: string;
  /** Icono adicional a mostrar junto al nombre */
  nameIcon?: ReactNode;
  /** Callback al hacer click en el nombre */
  onNameClick?: () => void;
  /** Callback al hacer click en el botón de mensaje */
  onMessageClick?: () => void;
}

/**
 * MxPersonCard
 *
 * Componente genérico para mostrar información de una persona con:
 * - Avatar con iniciales
 * - Nombre clickeable con línea roja decorativa
 * - Badge de estado opcional
 * - Rol/etiqueta
 * - Teléfono
 * - Botón de mensaje
 */
export const MxOwnerCard = ({
  name,
  role,
  phone,
  email,
  avatarUrl,
  nameIcon,
  onNameClick,
  onMessageClick
}: IMxPersonCardProps) => {
  const handleMessageClick = () => {
    if (onMessageClick) {
      onMessageClick();
    } else if (email) {
      window.location.href = `mailto:${email}`;
    }
  };

  return (
    <Grid container alignItems="center" justifyContent="space-between">
      {/* Avatar */}
      <Grid item xs="auto">
        <MxImageBlur
          alt={name}
          width={60}
          height={60}
          src={avatarUrl}
          borderRadius={12}
        />
      </Grid>

      <Grid container gap={0.5} item xs={8} textAlign="center">
        <Grid item xs={12}>
          <MxTypography
            variant="body2"
            color="primary.main"
            fontWeight={600}
            lines={2}
            sx={{
              cursor: onNameClick ? "pointer" : "default",
              "&:hover": onNameClick ? { textDecoration: "underline" } : {}
            }}
            onClick={onNameClick}
          >
            {name}
          </MxTypography>
          {nameIcon && (
            <MxIcons size={14} color="error.main">
              {nameIcon}
            </MxIcons>
          )}
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              width: "100%",
              height: "2px",
              bgcolor: "error.main",
              my: 0.5
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <MxTypography variant="caption" color="text.secondary">
            {role}
          </MxTypography>
        </Grid>
        <Grid item xs={12}>
          <MxTypography variant="body2" color="primary.main" fontWeight={600}>
            {phone}
          </MxTypography>
        </Grid>
      </Grid>

      <Grid item xs="auto">
        <IconButton
          size="small"
          onClick={handleMessageClick}
          sx={{ color: "primary.main" }}
        >
          <IconMessageFilled size={20} />
        </IconButton>
      </Grid>
    </Grid>
  );
};
